
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bell, Calendar, FileText, MessageSquare, Clock } from "lucide-react";

const DoctorNotifications = () => {
  // Sample notification data (would be from an API in a real app)
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'patient',
      title: 'New Patient Case',
      description: 'A new patient case has been assigned to you.',
      time: '10 minutes ago',
      read: false,
      link: '/case-file/123'
    },
    {
      id: 2,
      type: 'appointment',
      title: 'Upcoming Appointment',
      description: 'You have an appointment with Alex Johnson tomorrow at 2:00 PM.',
      time: '3 hours ago',
      read: true,
      link: '/appointments'
    },
    {
      id: 3,
      type: 'message',
      title: 'New Message',
      description: 'Maria Garcia sent you a message regarding her treatment plan.',
      time: '1 day ago',
      read: false,
      link: '/messages/456'
    },
    {
      id: 4,
      type: 'review',
      title: 'New Review',
      description: 'A patient left a 5-star review for your services.',
      time: '2 days ago',
      read: false,
      link: '/reviews'
    }
  ]);

  const [activeTab, setActiveTab] = useState('all');

  const markAsRead = (id: number) => {
    setNotifications(
      notifications.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(
      notifications.map((notification) => ({
        ...notification,
        read: true
      }))
    );
  };

  // Filter notifications based on active tab
  const filteredNotifications = activeTab === 'all' 
    ? notifications 
    : notifications.filter(notification => notification.type === activeTab);
  
  const unreadCount = notifications.filter(notification => !notification.read).length;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'patient':
        return <FileText className="w-5 h-5 text-blue-500" />;
      case 'appointment':
        return <Calendar className="w-5 h-5 text-green-500" />;
      case 'message':
        return <MessageSquare className="w-5 h-5 text-purple-500" />;
      default:
        return <Bell className="w-5 h-5 text-amber-500" />;
    }
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Notifications</h1>
          <p className="text-gray-500">Stay updated on your patient activity</p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" onClick={markAllAsRead}>Mark all as read</Button>
        )}
      </div>

      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 mb-6">
          <TabsTrigger value="all">
            All
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-2">
                {unreadCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="patient">Patients</TabsTrigger>
          <TabsTrigger value="appointment">Appointments</TabsTrigger>
          <TabsTrigger value="message">Messages</TabsTrigger>
          <TabsTrigger value="review">Reviews</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">
                {activeTab === 'all'
                  ? 'All Notifications'
                  : `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Notifications`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredNotifications.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="mx-auto h-12 w-12 text-gray-300" />
                  <p className="mt-4 text-gray-500">No notifications available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`flex p-4 rounded-md border ${
                        notification.read ? 'bg-white' : 'bg-blue-50'
                      }`}
                    >
                      <div className="mr-4 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between">
                          <h3 className="font-medium">{notification.title}</h3>
                          <span className="text-xs text-gray-500">
                            {notification.time}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.description}
                        </p>
                        <div className="mt-2 flex justify-between">
                          <Button variant="link" className="p-0 h-auto" asChild>
                            <a href={notification.link}>View Details</a>
                          </Button>
                          {!notification.read && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => markAsRead(notification.id)}
                            >
                              Mark as read
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DoctorNotifications;
